<template>
  <div
    :class="{
      'social-buttons-row': true,
      'social-buttons-row--edit-mode': props.isEditProfileMode,
    }"
  >
    <RequiredFieldNote v-if="props.enableRequiredFieldNote && !socialsLength" class="social-buttons-row__error-field-note">recommend at least 1</RequiredFieldNote>

    <div
      :class="{
        'social-buttons-row__buttons-wrap': true,
        'social-buttons-row__buttons-wrap--error': socialsLength < 1,
        'social-buttons-row__buttons-wrap--edit-mode': props.isEditProfileMode,
      }"
    >
      <input
        v-if="props.isEditProfileMode && !props.profileToBind.instagramHandle"
        class="global-text-input global-text-input--instagram-icon social-buttons-row__instagram-input"
        type="text"
        v-model="_instagramHandle"
        placeholder="Instagram handle"
        :disabled="isCheckingInstagram"
        @blur="onInstagramBlur"
      />
      <LoadingIcon v-if="isCheckingInstagram && !props.profileToBind.instagramHandle" class="social-buttons-row__instagram-loader" :size="18" />

      <SocialMediaLinksBar
        v-if="socialsLength"
        class="social-buttons-row__media-links-bar"
        :socialFields="props.profileToBind"
        :isWithSeparateEditButtons="props.isEditProfileMode"
        @editField="
          fieldName => {
            isSingleSocialFieldModalVisible = true;
            currentlyEditedField = fieldName;
          }
        "
      />

      <!--<SrpButton v-if="props.isEditProfileMode" class="social-buttons-row__edit-button" size="tiny" fill="outlined" @click="isSocialFieldsModalVisible = true">-->
      <!--  <template #icon>-->
      <!--    <IconEmbedded :name="socialsLength ? 'pencil_2' : 'plus_3'" style="margin-left: -3px" :size="21" color="rgba(84, 148, 150, 1)" />-->
      <!--  </template>-->
      <!--  {{ socialsLength ? "Edit" : "Add another social" }}-->
      <!--</SrpButton>-->

      <template v-if="props.isEditProfileMode">
        <SrpButton
          class="social-buttons-row__edit-button"
          v-for="socialName in socialNames.filter(s => {
            const emptyFields = [];
            if (!props.profileToBind.facebookURL) emptyFields.push('Facebook');
            if (!props.profileToBind.youtubeURL) emptyFields.push('YouTube');
            if (!props.profileToBind.tikTokHandle) emptyFields.push('TikTok');

            return emptyFields.includes(s);
          })"
          :key="socialName"
          size="tiny"
          fill="outlined"
          @click="
            () => {
              currentlyEditedField = {
                Facebook: 'facebookURL',
                YouTube: 'youtubeURL',
                TikTok: 'tikTokHandle',
              }[socialName];
              isSingleSocialFieldModalVisible = true;
            }
          "
        >
          Add
          <template #iconRight>
            <IconEmbedded
              :name="
                {
                  Facebook: 'facebook',
                  YouTube: 'youtube',
                  TikTok: 'tiktok',
                }[socialName]
              "
              style="margin: 0 -5px 0 -3px"
              :size="21"
              color="rgba(84, 148, 150, 1)"
            />
          </template>
        </SrpButton>
      </template>
    </div>

    <div v-if="showInstagramFeedback" class="social-buttons-row__validation">
      <LoadingIcon v-if="isCheckingInstagram && props.profileToBind.instagramHandle" :size="18" />
      <NoteWithIcon v-else-if="instagramState === 'invalid'" color="yellow" size="tiny">
        <template #text>Handle not found!</template>
      </NoteWithIcon>
      <div v-else-if="instagramTestUrl" class="social-buttons-row__found">
        <LocationAvatar v-if="props.profileToBind.instagramIconId" class="social-buttons-row__found-icon" :src="props.profileToBind.instagramIconId" :name="props.profileToBind.instagramHandle" :size="32" />
        <a class="social-buttons-row__test-link" :href="instagramTestUrl" target="_blank" rel="noopener noreferrer">
          <span class="social-buttons-row__test-link-text">{{ instagramTestUrl }}</span>
          <IconEmbedded name="external-link_2-5" :size="15" color="rgba(0, 0, 0, 0.3)" />
        </a>
      </div>
    </div>

    <SrpModal v-model:isVisible="isSingleSocialFieldModalVisible" size="small">
      <template #content>
        <SocialMediaInputs ref="singleFieldInputs" :socialFields="props.profileToBind" :creatorId="props.profileToBind.sherpaId" :isPersonalWebsiteFieldsVisible="false" :showOnly="currentlyEditedField" />
      </template>
      <template #footer>
        <SrpButton @click="isSingleSocialFieldModalVisible = false" style="margin-right: 12px" fill="outlined" color="gray">Cancel</SrpButton>
        <SrpButton @click="onSingleFieldDone">
          <template #icon>
            <IconEmbedded name="save_2" style="margin-right: 1px" :size="23" color="rgba(255, 255, 255, 0.4)" />
          </template>
          Done
        </SrpButton>
      </template>
    </SrpModal>

    <SrpModal v-model:isVisible="isSocialFieldsModalVisible">
      <template #header>
        <h1 class="global-h1">Social Media buttons</h1>
      </template>
      <template #content>
        <SocialMediaInputs :socialFields="props.profileToBind" :creatorId="props.profileToBind.sherpaId" :isPersonalWebsiteFieldsVisible="false" />
      </template>
      <template #footer>
        <SrpButton @click="isSocialFieldsModalVisible = false" style="margin-right: 12px" fill="outlined" color="gray">Cancel</SrpButton>
        <SrpButton @click="isSocialFieldsModalVisible = false">
          <template #icon>
            <IconEmbedded name="save_2" style="margin-right: 1px" :size="23" color="rgba(255, 255, 255, 0.4)" />
          </template>
          Done
        </SrpButton>
      </template>
    </SrpModal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import LocationAvatar from "@components/SocialLocations/LocationAvatar.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import RequiredFieldNote from "@components/ui/RequiredFieldNote.vue";
import SocialMediaInputs from "@components/SocialMediaInputs.vue";
import SocialMediaLinksBar from "@components/SocialMediaLinksBar.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpModal from "@components/ui/SrpModal.vue";

// Types
import { ICreatorProfileViewModel } from "@views/CreatorProfilePublic/creatorProfileViewModel";
import { SocialMediaFieldsAndWebsite } from "@components/SocialMediaInputs.vue";
import SocialFieldInputCleaner, { buildInstagramProfileUrl } from "@logic/SocialFieldInputCleaner";
import ProfileRepo, { CreatorSocialValidationResult, CreatorSocialValidationStatus } from "@logic/ProfileRepo";

const props = withDefaults(
  defineProps<{
    isEditProfileMode: boolean;
    enableRequiredFieldNote: boolean;
    profileToBind: ICreatorProfileViewModel | null;
  }>(),
  {
    isEditProfileMode: false,
    enableRequiredFieldNote: false,
    profileToBind: null,
  }
);

const emit = defineEmits<{
  (e: "saveUserProfile"): void;
}>();

// Calc socials length ========================================================
const socialsLength = computed(() => {
  // prettier-ignore
  return [
    props.profileToBind?.instagramHandle,
    props.profileToBind?.facebookURL,
    props.profileToBind?.youtubeURL,
    props.profileToBind?.tikTokHandle,
    props.profileToBind?.twitterHandle
  ].filter(f => Boolean(f)).length;
});

// Toggle SocialFields modal ==================================================
const isSocialFieldsModalVisible = ref(false);

// InstagramHandle local copy =================================================
const _instagramHandle = ref<string>("");
const isCheckingInstagram = ref(false);
const instagramState = ref<CreatorSocialValidationStatus | "unchecked">("unchecked");
const lastCheckedInstagram = ref("");
let instagramValidationSequence = 0;

const instagramTestUrl = computed(() => buildInstagramProfileUrl(props.profileToBind?.instagramHandle));
const showInstagramFeedback = computed(
  () => props.isEditProfileMode && (isCheckingInstagram.value || instagramState.value === "invalid" || Boolean(props.profileToBind?.instagramHandle))
);

function restoreInstagramStateFromProfile() {
  const profile = props.profileToBind;
  if (!profile) return;
  _instagramHandle.value = profile.instagramHandle;
  if (profile.instagramValidatedAt || profile.instagramIconId) {
    instagramState.value = "valid";
    lastCheckedInstagram.value = profile.instagramHandle ?? "";
  } else {
    instagramState.value = "unchecked";
    lastCheckedInstagram.value = "";
  }
}

function applyInstagramValidation(handle: string, result: CreatorSocialValidationResult) {
  const profile = props.profileToBind;
  profile.instagramHandle = result.status === "valid" && result.username ? result.username : handle;
  if (result.status === "valid") {
    profile.instagramIconId = result.iconId ?? null;
    profile.instagramValidatedAt = result.validatedAt ?? new Date().toISOString();
    instagramState.value = "valid";
  } else {
    profile.instagramIconId = null;
    profile.instagramValidatedAt = null;
    instagramState.value = result.status;
  }
}

async function onInstagramBlur() {
  const handle = SocialFieldInputCleaner.cleanInstagramHandle(_instagramHandle.value);
  _instagramHandle.value = handle;
  const profile = props.profileToBind;
  if (!handle) {
    profile.instagramHandle = null;
    profile.instagramIconId = null;
    profile.instagramValidatedAt = null;
    instagramState.value = "unchecked";
    lastCheckedInstagram.value = "";
    emit("saveUserProfile");
    return;
  }
  if (handle === lastCheckedInstagram.value) {
    if (!profile.instagramHandle) {
      profile.instagramHandle = handle;
      emit("saveUserProfile");
    }
    return;
  }
  const sequence = ++instagramValidationSequence;
  isCheckingInstagram.value = true;
  const result = await ProfileRepo.validateInstagram(profile.sherpaId, handle, true);
  if (sequence !== instagramValidationSequence) return;
  isCheckingInstagram.value = false;
  applyInstagramValidation(handle, result);
  lastCheckedInstagram.value = profile.instagramHandle ?? handle;
  _instagramHandle.value = "";
  emit("saveUserProfile");
}

watch(() => props.profileToBind, restoreInstagramStateFromProfile, { immediate: true });
watch(
  () => [props.profileToBind?.instagramIconId, props.profileToBind?.instagramValidatedAt] as const,
  ([iconId, validatedAt]) => {
    if (iconId || validatedAt) {
      instagramState.value = "valid";
      lastCheckedInstagram.value = props.profileToBind?.instagramHandle ?? "";
    }
  }
);
watch(
  () => props.profileToBind?.instagramHandle,
  handle => {
    if (!handle) {
      lastCheckedInstagram.value = "";
      if (instagramState.value !== "invalid") instagramState.value = "unchecked";
    }
  }
);

// Single social field edit modal =============================================
const socialNames = ["Facebook", "YouTube", "TikTok"] as const;
type SocialName = (typeof socialNames)[number];

const isSingleSocialFieldModalVisible = ref<boolean>(false);
const currentlyEditedField = ref<keyof SocialMediaFieldsAndWebsite | null>(null);
const singleFieldInputs = ref<{ validateVisibleFields: () => Promise<void> } | null>(null);

async function onSingleFieldDone() {
  await singleFieldInputs.value?.validateVisibleFields();
  isSingleSocialFieldModalVisible.value = false;
}

// Cleans up the Instagram field value ========================================
watch(_instagramHandle, newValue => (_instagramHandle.value = SocialFieldInputCleaner.cleanInstagramHandle(newValue)));
</script>

<style scoped lang="scss">
// Social buttons row =========================================================
.social-buttons-row {
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  &__error-field-note {
    margin-bottom: 14px;
  }

  &__buttons-wrap {
    width: max-content;
    max-width: 100%;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 8px;
    position: relative;
    z-index: 0;

    &::before {
      content: "";
      width: calc(100% + 20px);
      height: calc(100% + 18px);
      box-sizing: border-box;
      border: 1px rgba(180, 180, 180, 1) dashed;
      border-radius: 6px;
      position: absolute;
      inset: -9px auto auto -10px;
      z-index: -1;
      background: #f3f3f3;
      opacity: 0;
    }

    &--edit-mode {
      max-width: calc(100% - 20px);

      &::before {
        opacity: 1;
      }
    }

    &--error {
      &::before {
        box-shadow: 0 0 0 5px rgba(188, 73, 73, 0.35);
      }
    }
  }

  &__instagram-input {
    width: 260px;
    max-width: 100%;
    flex: 0 0 auto;
  }

  &__instagram-loader {
    flex: 0 0 auto;
  }

  &__validation {
    margin-top: 14px;
    width: 100%;
    display: flex;
    justify-content: center;
  }

  &__found {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  &__found-icon {
    flex: 0 0 auto;
  }

  &__test-link {
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 3px;
  }

  &__test-link-text {
    color: rgba(17, 134, 137, 1);
    text-decoration: underline;
    text-decoration-color: rgba(17, 134, 137, 0.5);
    text-underline-offset: 2px;
    text-decoration-thickness: 1px;
    word-break: break-all;
  }

  &__test-link:hover &__test-link-text {
    text-decoration: none;
  }

  &__media-links-bar {
    min-width: 0;
    max-width: 100%;
  }

  &__edit-button {
    &::before,
    &:hover::before {
      background: rgba(255, 255, 255, 1);
    }
  }
}
</style>
